import React, { Fragment, useEffect } from 'react';
import { Tooltip } from 'react-tippy';
import { FiCheck } from 'react-icons/fi';
import Button from '../../Button';
import { papersignFeatures } from '../../../constants/features';
import pricingPlans from '../../../constants/plans';

const plans = [
  pricingPlans.papersignFree,
  pricingPlans.papersignPro,
  pricingPlans.papersignEnterprise,
];
const discount = pricingPlans.papersignDiscount;

export default function PapersignPricing({ annual, showFeatures = true }) {
  useEffect(() => {
    if (!window.Paperform) {
      const script = document.createElement('script');
      script.src = 'https://paperform.co/__embed.min.js';
      script.async = true;
      document.body.appendChild(script);
    }
  }, []);
  return (
    <>
      <table
        id="pricing"
        className="lg:leading-[25px] text-[13px] leading-[18px] lg:text-[16px] min-w-[900px] table-fixed"
        style={{
          maxWidth: showFeatures ? '100%' : '700px',
        }}
      >
        <tbody>
          {showFeatures || discount.text ? (
            <tr style={{ verticalAlign: 'top' }}>
              {showFeatures ? <td className="w-[200px] lg:w-auto" /> : null}
              {plans.map((plan) =>
                plan.discountPrice && discount.text ? (
                  <td className="w-auto bg-midnight text-white font-sonoma border-l-8 border-r-8 border-white flex-col py-2 px-3 text-center">
                    <strong>{discount.text}</strong>
                  </td>
                ) : (
                  <td className="w-auto" />
                ),
              )}
            </tr>
          ) : null}
          <tr style={{ verticalAlign: 'top' }}>
            {showFeatures ? <td className="w-[200px] lg:w-auto" /> : null}
            {plans.map((plan) => (
              <td
                key={plan.title}
                className={`font-sonoma border-r-8 border-white flex-col py-6 pb-0 px-3 text-center ${plan.tailwind}`}
              >
                <div className="mb-6 mt-3 h-[124px] flex items-center justify-center">
                  {plan.icon()}
                </div>
                <div className="text-lg lg:text-2xl mb-3">{plan.title}</div>
                {annual ? (
                  <div>
                    {plan.free || plan.enterprise ? (
                      <>
                        <div className="text-base lg:text-lg mb-1">&nbsp;</div>
                        <div className="lg:mb-3">&nbsp;</div>
                      </>
                    ) : (
                      <>
                        <div className="text-base lg:text-lg mb-1">
                          {plan.discountPriceAnnual ? (
                            <span className="line-through mr-2 opacity-60">
                              {plan.price.annualMonthly}
                            </span>
                          ) : null}
                          {plan.discountPriceAnnual || plan.price.annualMonthly}
                          /user/month
                        </div>
                        {plan.price.annual || plan.discountPriceAnnualTotal ? (
                          <div className="lg:mb-3">
                            Billed as{' '}
                            {plan.discountPriceAnnualTotal || plan.price.annual}
                            /user/year
                          </div>
                        ) : null}
                      </>
                    )}
                  </div>
                ) : (
                  <div>
                    {plan.free || plan.enterprise ? (
                      <>
                        <div className="text-base lg:text-lg mb-1">&nbsp;</div>
                        <div className="lg:mb-3">&nbsp;</div>
                      </>
                    ) : (
                      <>
                        <div className="text-base lg:text-lg mb-1">
                          {plan.discountPrice ? (
                            <span className="line-through mr-2 opacity-60">
                              {plan.price.monthly}
                            </span>
                          ) : null}
                          {plan.discountPrice || plan.price.monthly}
                          /user/month
                        </div>
                        <div className="lg:mb-3">Billed monthly</div>
                      </>
                    )}
                  </div>
                )}

                <div className="font-worksans text-sm text-black/50 px-4 mt-3 mb-7 hidden lg:block">
                  {plan.description}
                </div>
              </td>
            ))}
          </tr>
          {showFeatures ? (
            <>
              <tr>
                <td className="w-[200px] lg:w-auto px-3 py-1 opacity-60 italic">
                  *All pricing is in USD, and exclude any sales taxes, including
                  VAT.
                </td>

                {plans.map((plan) => (
                  <td
                    key={plan.title}
                    style={{
                      paddingBottom: showFeatures ? 'calc(0.725rem - 1px)' : 40,
                    }}
                    className={`font-sonoma border-r-8 border-white flex-col px-3 text-center ${plan.tailwind}`}
                  >
                    {plan.enterprise ? (
                      <Button
                        href="#chat=I want to talk to sales about Papersign Enterprise"
                        midnight
                        solid
                      >
                        Talk to Sales
                      </Button>
                    ) : (
                      <Button midnight href={plan.link} solid>
                        Try It
                      </Button>
                    )}
                  </td>
                ))}
              </tr>
              {Object.keys(papersignFeatures).map((group) => (
                <Fragment key={group}>
                  {group.trim() ? (
                    <tr className="font-semibold text-base">
                      <td className="px-3 py-2 pt-12">{group}</td>
                      {plans.map((plan) => (
                        <td
                          key={plan.title}
                          style={{
                            backgroundColor: `rgba(${plan.colorRGB},.3)`,
                          }}
                          className="px-3 border-r-8 border-white mr-2"
                        />
                      ))}
                    </tr>
                  ) : null}
                  {Object.keys(papersignFeatures[group]).map((feature) => {
                    const featureObject = papersignFeatures[group][feature];
                    return (
                      <Tooltip
                        tag="tr"
                        key={feature}
                        animation="perspective"
                        className="group"
                        disabled={!featureObject.help_text}
                        theme="light"
                        html={
                          <div className="text-[12px] leading-tight py-1">
                            {featureObject.help_text}
                          </div>
                        }
                        style={{ display: 'table-row' }}
                        position="bottom"
                        trigger="mouseenter"
                        followCursor
                      >
                        <td className="px-3 py-1 bg-white group-hover:bg-stone transition-all transition-500">
                          {featureObject.title}
                        </td>
                        {plans.map((plan) => (
                          <td
                            key={plan.title}
                            style={
                              ['boolean', 'undefined'].includes(
                                typeof plan.features[feature],
                              ) && !plan.features[feature]
                                ? {
                                    backgroundColor: `rgba(${plan.colorRGB},.8)`,
                                  }
                                : null
                            }
                            className={`px-3 ${plan.tailwind} transition-all transition-500 border-r-8  border-white font-sonoma text-center`}
                          >
                            {['string', 'object', 'number'].includes(
                              typeof plan.features[feature],
                            )
                              ? plan.features[feature]
                              : null}
                            {['boolean'].includes(
                              typeof plan.features[feature],
                            ) ? (
                              plan.features[feature] ? (
                                <FiCheck size={22} />
                              ) : null
                            ) : null}
                          </td>
                        ))}
                      </Tooltip>
                    );
                  })}
                </Fragment>
              ))}
            </>
          ) : null}
          <tr>
            {showFeatures ? <td className="w-[200px] lg:w-auto" /> : null}
            {plans.map((plan) => (
              <td
                key={plan.title}
                className={`font-sonoma border-r-8 border-white py-6 px-3 text-center ${plan.tailwind}`}
              >
                {plan.enterprise ? (
                  <Button
                    midnight
                    href={`#chat=I want to talk to sales about Papersign Enterprise`}
                    solid
                    fullwidth
                  >
                    Talk to Sales
                  </Button>
                ) : (
                  <Button midnight href={plan.link} solid fullwidth>
                    Try Free Now
                  </Button>
                )}
                <div className="text-xs mt-3 opacity-50">No Credit Card</div>
              </td>
            ))}
          </tr>
        </tbody>
      </table>
      {discount.terms ? (
        <div>
          <small className="text-xs opacity-60">* {discount.terms}</small>
        </div>
      ) : null}
    </>
  );
}
